import {
  Button,
  Dialog,
  PlainButton,
  TextInput,
  NativeSelectUnstyled,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useState } from "react";
import { v4 as uuid4 } from "uuid";
import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { useInvoiceFormOptions } from "@jugl-web/domain-resources/invoices/hooks/useInvoiceFormOptions";
import { ReactComponent as TaxIcon } from "./assets/tax.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

const MAX_NUMBER_LENGTH = 3;

type TaxFieldDialogProps = {
  isDialogOpen: boolean;
  onClose: () => void;
  onSubmit: (field: InvoiceTaxRow) => void;
  initialField?: InvoiceTaxRow;
  currency: string;
};

type FormFields = {
  name: string;
  percentage: string;
};

export const TaxFieldDialog = ({
  isDialogOpen,
  onClose,
  onSubmit,
  initialField,
  currency,
}: TaxFieldDialogProps) => {
  const { t } = useTranslations();
  const [type, setType] = useState<"percentage" | "currency">("percentage");

  const schema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .test(
        "required",
        t({
          id: "common.validation.notEmpty",
          defaultMessage: "Value cannot be empty",
        }),
        (value) => !!value
      )
      .test(
        "max-length",
        t({
          id: "invoices.settings.businessInformation.taxSettings.maxLength",
          defaultMessage: "Tax name must be 50 characters or fewer",
        }),
        (value) => !value || value.length <= 50
      ),
    percentage: yup.string().test(
      "max-hundred",
      t({
        id: "common.validation.maxValue100",
        defaultMessage: "Value cannot exceed 100",
      }),
      (value) => {
        if (!value) return true;
        return Number(value) <= 100;
      }
    ),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      name: initialField?.name || "",
      percentage: initialField?.percentage?.toString() || "",
    },
  });

  const { taxUnits } = useInvoiceFormOptions({
    currency,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = (data: FormFields) => {
    onSubmit({
      id: initialField?.id || uuid4(),
      name: data.name,
      percentage: Number(data.percentage),
      default: initialField?.default || false,
    });
    reset();
    onClose();
  };

  return (
    <Dialog isOpen={isDialogOpen} onClose={handleClose} className="w-[480px]">
      <header className="rounded-t-3xl">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2 font-secondary text-sm font-semibold text-dark">
            <TaxIcon />
            {initialField
              ? t({
                  id: "invoices.settings.businessInformation.taxSettings.editTax",
                  defaultMessage: "Edit Tax",
                })
              : t({
                  id: "invoices.settings.businessInformation.taxSettings.addTax",
                  defaultMessage: "Add Tax",
                })}
          </div>
          <PlainButton onClick={handleClose}>
            <CloseIcon />
          </PlainButton>
        </div>
        <div className="h-px w-full bg-dark-100" />
      </header>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <div className="mt-8 space-y-8 px-6">
          <TextInput
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            label={t({
              id: "invoices.settings.businessInformation.taxSettings.name",
              defaultMessage: "Label",
            })}
            isRequired
            errorMessage={errors.name?.message}
            {...register("name")}
          />

          <TextInput
            type="number"
            placeholder={t({
              id: "common.enter",
              defaultMessage: "Enter",
            })}
            label={t({
              id: "invoices.settings.businessInformation.taxSettings.percentage",
              defaultMessage: "Rate",
            })}
            isRequired
            errorMessage={errors.percentage?.message}
            {...register("percentage", {
              onChange: (e) => {
                const { value } = e.target;
                if (value.length > MAX_NUMBER_LENGTH) {
                  e.target.value = value.slice(0, MAX_NUMBER_LENGTH);
                }
              },
            })}
            onKeyDown={(e) => {
              const notAllowedCharacters = ["e", "E", "+", "-"];
              if (notAllowedCharacters.includes(e.key)) e.preventDefault();
            }}
            endContent={
              <NativeSelectUnstyled
                className="w-16 font-secondary text-base text-dark"
                value={type}
                onChange={(e) => {
                  const { value } = e.target;
                  setType(value as "percentage" | "currency");
                }}
              >
                {taxUnits.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.value}
                  </option>
                ))}
              </NativeSelectUnstyled>
            }
          />
        </div>

        <div className="mt-4 flex w-full items-center justify-between gap-4 p-6">
          <Button
            className="w-full shrink"
            variant="tertiary"
            type="button"
            onClick={handleClose}
          >
            {t({
              id: "common.close",
              defaultMessage: "Close",
            })}
          </Button>
          <Button className="w-full shrink" type="submit">
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
